<template>
  <b-modal
    id="CreateProject"
    size="lg"
    no-stacking
    header-class="modal-class"
    body-class="modal-class"
    @hidden="modalClose"
  >
    <b-row align-h="center">
      <div class="stepper-wrapper">
        <div
          class="stepper-item"
          :class="{
            'completed': value > 0,
            'active': value === 0
          }"
        >
          <div class="step-counter" />
          <div class="step-name text-center">
            Project Overview
          </div>
        </div>
        <div
          class="stepper-item"
          :class="{
            'completed': value > 1,
            'active': value === 1,
            'text-muted': value < 1,
          }"
        >
          <div class="step-counter" />
          <div class="step-name text-center">
            Project Details
          </div>
        </div>
        <div
          class="stepper-item"
          :class="{
            'completed': value > 2,
            'active': value === 2,
            'text-muted': value < 2,
          }"
        >
          <div class="step-counter" />
          <div class="step-name text-center">
            Recruiter Details
          </div>
        </div>
      </div>
    </b-row>
    <b-row class="mt-5">
      <b-col>
        <label
          v-if="value == 0"
          class="h7 text-center"
        >
          Add Project Overview
        </label>
        <label
          v-if="value == 1"
          class="h7 text-center"
        >
          Add Project Details
        </label>
        <label
          v-if="value == 2"
          class="h7 text-center"
        >
          Add Recruiter Details
        </label>
      </b-col>
    </b-row>
    <b-row
      class="mt-3"
      align-h="center"
    >
      <b-card class="form">
        <b-form
          class="text-left"
        >
          <div v-if="value === 0">
            <b-form-group
              v-if="isAdmin && !editProject"
              id="input-group-client"
              label="Client*"
              label-for="input-client"
            >
              <FormTags
                v-model="form.business_id"
                style="width: 74.5%"
                name="business_id"
                :options="businessOptions"
                button-text="Choose Client"
                required
                :limit="1"
              />
            </b-form-group>
            <b-form-group
              v-if="isAdmin && !editProject"
              id="input-group-primary-contact"
              label="Primary Contact*"
              label-for="input-primary-contact"
            >
              <FormTags
                v-model="form.primary_contacts"
                name="primary_contacts"
                :options="primaryContactOptions"
                button-text="Choose Primary Contact"
                required
              >
                <template #item="{ option }">
                  <b>{{ option.full_name }}</b><hr>email:<b>{{ option.email }}</b>
                </template>
              </FormTags>
            </b-form-group>
            <b-form-group
              id="input-group-title"
              label="Project Title*"
              label-for="input-title"
            >
              <b-form-input
                id="input-title"
                v-model="form.title"
                name="title"
                placeholder="Javascript Developer"
                required
              />
              <label
                v-if="errors.title"
                class="hint-local font-weight-light"
              >
                <i
                  class="vertical-bottom ri-error-warning-line"
                />
                {{ errors.title }}
              </label>
            </b-form-group>
            <b-form-group
              v-if="secondaryContactOptions.length && !editProject"
              id="input-group-secondary-contact"
              label="Add More Users"
              label-for="input-secondary-contact"
            >
              <FormTags
                v-model="form.secondary_contacts"
                :search-enabled="false"
                name="secondary_contacts"
                :options="secondaryContactOptions"
                button-text="Choose User"
              />
            </b-form-group>
            <b-form-group
              id="input-group-description"
              label="Project Description*"
              label-for="input-description"
            >
              <b-form-textarea
                id="input-description"
                v-model="form.description"
                name="description"
                placeholder="For example:

We are looking for a recruiter who could help us hire the Country Head of Angola. The recruiter we are looking for needs language skills (ENG & POR) for sourcing & approaching those candidates (ideally in their mother tongue = POR). The person who needs to be hired for the Head of Angola role will need to be located in Angola or ready to relocate there."
                rows="6"
                required
              />
              <label
                v-if="errors.description"
                class="hint-local font-weight-light"
              >
                <i
                  class="vertical-bottom ri-error-warning-line"
                />
                {{ errors.description }}
              </label>
            </b-form-group>
            <b-form-group
              id="input-group-vacancies"
              label="Number Of Vacancies*"
              label-for="input-vacancies"
            >
              <b-form-input
                id="input-vacancies"
                v-model="form.vacancies"
                class="short-field"
                name="vacancies"
                placeholder="2 roles/positions"
                required
              />
              <label
                v-if="errors.vacancies"
                class="hint-local font-weight-light"
              >
                <i
                  class="vertical-bottom ri-error-warning-line"
                />
                {{ errors.vacancies }}
              </label>
            </b-form-group>
            <b-form-group
              id="input-group-urgency"
              label="Project Urgency*"
              label-for="input-urgency"
            >
              <b-form-select
                id="input-urgency"
                v-model="form.urgency"
                class="short-field"
                name="urgency"
                :options="urgencyOptions"
                required
              />
              <br v-if="errors.urgency">
              <label
                v-if="errors.urgency"
                class="hint-local font-weight-light"
              >
                <i
                  class="vertical-bottom ri-error-warning-line"
                />
                {{ errors.urgency }}
              </label>
            </b-form-group>
            <b-form-group
              id="input-group-roles"
              label="Job Category*"
              label-for="input-roles"
            >
              <BadgeCheckbox
                v-model="form.roles"
                name="roles"
                :options="roleOptions"
                button-text="Choose Job Category"
                required
              />
              <label
                v-if="errors.roles"
                class="hint-local font-weight-light"
              >
                <i
                  class="vertical-bottom ri-error-warning-line"
                />
                {{ errors.roles }}
              </label>
            </b-form-group>
          </div>
          <div v-if="value === 1">
            <b-form-group
              id="input-group-scope-work"
              label="Employment Type*"
              label-for="input-scope-work"
            >
              <BadgeCheckbox
                v-model="form.working_types"
                name="working_types"
                :options="workingTypeOptions"
                button-text="Choose Working Types"
                required
              />
              <label
                v-if="errors.working_types"
                class="hint-local font-weight-light"
              >
                <i
                  class="vertical-bottom ri-error-warning-line"
                />
                {{ errors.working_types }}
              </label>
            </b-form-group>
            <b-form-group
              id="input-group-previous-activities"
              label="Previous Activities To Find Candidates*"
              label-for="input-previous-activities"
            >
              <BadgeCheckbox
                v-model="form.previous_activities"
                name="previous_activities"
                :options="previousActivitiesOptions"
                button-text="Choose Activities"
                required
              />
              <label
                v-if="errors.previous_activities"
                class="hint-local font-weight-light"
              >
                <i
                  class="vertical-bottom ri-error-warning-line"
                />
                {{ errors.previous_activities }}
              </label>
            </b-form-group>
            <b-form-group
              id="regions"
              label="Preferred Regions Where The Candidate Should Be"
              label-for="input-roles"
            >
              <BadgeCheckbox
                v-model="form.regions"
                name="regions"
                :options="regionsOptions"
                button-text="Choose Regions"
              />
              <label
                v-if="errors.regions"
                class="hint-local font-weight-light"
              >
                <i
                  class="vertical-bottom ri-error-warning-line"
                />
                {{ errors.regions }}
              </label>
            </b-form-group>
            <b-form-group
              id="input-group-preferred_locations"
              label="Preferred Countries Or Cities"
              label-for="input-preferred_locations"
            >
              <b-form-textarea
                id="input-preferred_locations"
                v-model="form.preferred_locations"
                name="preferred_locations"
                placeholder="Where should the candidate be located? If you don't have a location preference, leave blank."
              />
              <label
                v-if="errors.preferred_locations"
                class="hint-local font-weight-light"
              >
                <i
                  class="vertical-bottom ri-error-warning-line"
                />
                {{ errors.preferred_locations }}
              </label>
            </b-form-group>
            <b-form-group
              id="input-group-salary"
              label="Salary Range"
              label-for="input-salary"
              class="mb-0"
            >
              <b-form-input
                id="input-salary"
                v-model="form.salary"
                class="short-field"
                name="salary"
                placeholder="2000 -- 3000 net wage"
              />
              <label
                v-if="errors.salary"
                class="hint-local font-weight-light"
              >
                <i
                  class="vertical-bottom ri-error-warning-line"
                />
                {{ errors.salary }}
              </label>
            </b-form-group>
            <b-form-group
              id="input-group-link"
              label="Job Ad URL"
              label-for="input-link"
              class="mt-3"
            >
              <b-form-input
                id="input-link"
                v-model="form.link"
                class="short-field"
                name="link"
                type="url"
                placeholder="URL/Link to company's job ad"
              />
              <label
                v-if="errors.link"
                class="hint-local font-weight-light"
              >
                <i
                  class="vertical-bottom ri-error-warning-line"
                />
                {{ errors.link }}
              </label>
            </b-form-group>
            <b-form-group
              id="attachments"
              label="Job Description file(PDF, Doc, Docx, JPG, PNG)"
              label-for="input-link"
            >
              <b-form-file
                v-model="attachments"
                class="short-field"
                :state="Boolean((form.attachments || []).length)"
                accept=".jpg, .png, .pdf, .doc, .docx"
                name="Job Description File"
                size="sm"
                placeholder="Choose a file or drop it here..."
                drop-placeholder="Drop file here..."
                @change="uploadFile"
              />
            </b-form-group>
            <b-form-group
              id="input-group-office"
              label="Office Location"
              label-for="input-office"
            >
              <b-form-input
                id="input-office"
                v-model="form.office"
                class="short-field"
                name="office"
                placeholder="Tartu/Tallinn or remote"
              />
              <label
                v-if="errors.office"
                class="hint-local font-weight-light"
              >
                <i
                  class="vertical-bottom ri-error-warning-line"
                />
                {{ errors.office }}
              </label>
            </b-form-group>
          </div>
          <div v-if="value === 2">
            <b-form-group
              id="input-group-scope-work"
              label="Scope Of Work*"
              label-for="input-scope-work"
            >
              <BadgeCheckbox
                v-model="form.scopes"
                name="scopes"
                :options="scopesOptions"
                button-text="Choose Scope"
                required
              />
              <label
                v-if="errors.scopes"
                class="hint-local font-weight-light"
              >
                <i
                  class="vertical-bottom ri-error-warning-line"
                />
                {{ errors.scopes }}
              </label>
            </b-form-group>
            <b-form-group
              id="input-group-services"
              label="Services Needed*"
              label-for="input-services"
            >
              <BadgeCheckbox
                v-model="form.services"
                name="services"
                :options="servicesOptions"
                button-text="Choose Services"
                required
              />
              <label
                v-if="errors.services"
                class="hint-local font-weight-light"
              >
                <i
                  class="vertical-bottom ri-error-warning-line"
                />
                {{ errors.services }}
              </label>
            </b-form-group>
            <b-form-group
              id="input-group-recruiter_location"
              label="Recruiter Location*"
              label-for="input-recruiter_location"
            >
              <b-form-select
                id="input-recruiter_location"
                v-model="form.recruiter_location"
                class="short-field"
                name="recruiter_location"
                :options="RecruiterLocationOptions"
                required
              />
              <br v-if="errors.recruiter_location">
              <label
                v-if="errors.recruiter_location"
                class="hint-local font-weight-light"
              >
                <i
                  class="vertical-bottom ri-error-warning-line"
                />
                {{ errors.recruiter_location }}
              </label>
            </b-form-group>
            <b-form-group
              id="input-group-pricing-model"
              label="Pricing Model Preference*"
              label-for="input-pricing-model"
            >
              <BadgeCheckbox
                v-model="form.pricing_models"
                name="pricing_models"
                :options="pricingModelsOptions"
                button-text="Choose Pricing Model(s)"
                required
              />
              <label
                v-if="errors.pricing_models"
                class="hint-local font-weight-light"
              >
                <i
                  class="vertical-bottom ri-error-warning-line"
                />
                {{ errors.pricing_models }}
              </label>
            </b-form-group>
            <b-form-group
              id="input-group-budget-range"
              label="Budget Range*"
              label-for="input-budget-range"
              class="budget-range"
            >
              <label
                class="mb-0 h9"
                style="vertical-align:super"
              >
                This information is not displayed for the recruiter/agency
              </label>
              <BadgeCheckbox
                v-model="form.fee_ranges"
                name="fee_ranges"
                :options="feeRangeOptions"
                button-text="Choose Fee Range(s)"
                required
              />
              <label
                v-if="errors.fee_ranges"
                class="hint-local font-weight-light"
              >
                <i
                  class="vertical-bottom ri-error-warning-line"
                />
                {{ errors.fee_ranges }}
              </label>
            </b-form-group>
            <b-form-group
              id="input-group-most_important"
              label="Recruiter Description*"
              label-for="input-most_important"
            >
              <b-form-textarea
                id="input-most_important"
                v-model="form.most_important"
                name="most_important"
                placeholder="Describe any hard skills, soft skills, experience or knowledge your recruiter should have."
                required
              />
              <label
                v-if="errors.most_important"
                class="hint-local font-weight-light"
              >
                <i
                  class="vertical-bottom ri-error-warning-line"
                />
                {{ errors.most_important }}
              </label>
            </b-form-group>
          </div>
        </b-form>
      </b-card>
    </b-row>
    <template #modal-footer>
      <b-row>
        <b-col>
          <span>
            <b-btn
              v-if="value > 0"
              class="mr-4 pt-1"
              variant="text"
              style="color: #474546;"
              @click="value--"
            >
              Back
            </b-btn>
            <b-btn
              v-if="value !== 2"
              variant="primary"
              style="border-radius: 4px;"
              @click="value++"
            >
              Next
            </b-btn>
            <b-btn
              v-if="value === 2 && !editProject"
              variant="primary"
              style="border-radius: 4px;"
              @click="publish"
            >
              Create Project
            </b-btn>
            <b-btn
              v-if="value === 2 && editProject"
              variant="primary"
              style="border-radius: 4px;"
              @click="update"
            >
              Update
            </b-btn>
          </span>
        </b-col>
      </b-row>
    </template>
  </b-modal>
</template>

<script>
import axios from '@/helpers/axios'
import FormTags from '@/components/FormTags.vue'
import BadgeCheckbox from '@/components/form/BadgeCheckbox'
import { mapState } from 'pinia'
import { CLOSING_REASONS, PROJECT_STATUSES, statusToArray } from '@/helpers/statuses'
import { useAuthStore } from '@/stores/auth'

const GEO_REGIONS_ORDER = {
  Globally: 1,
  'North America': 2,
  'Central America': 3,
  'South America': 4,
  'The Caribbean': 5,
  Africa: 6,
  'Western Europe': 7,
  'Northern Europe': 8,
  'Eastern Europe': 9,
  'Southern Europe': 10,
  'Middle East': 11,
  'Central Asia': 12,
  'East Asia': 13,
  'South Asia': 14,
  'Southeast Asia': 15,
  Oceania: 16,
}

export default {
  name: 'CreateProject',
  components: {
    FormTags,
    BadgeCheckbox,
  },
  data () {
    return {
      value: 0,
      attachments: null,
      editProject: '',
      form: {
        status: 'Draft',
        attachments: [],
        previous_activities: [],
      },
      fieldStepMap: {
        // Step 0
        title: 0,
        secondary_contacts: 0,
        description: 0,
        vacancies: 0,
        urgency: 0,
        roles: 0,

        // Step 1
        working_types: 1,
        previous_activities: 1,
        regions: 1,
        salary: 1,
        link: 1,
        attachments: 1,
        office: 1,

        // Step 2
        scopes: 2,
        services: 2,
        recruiter_location: 2,
        fee_ranges: 2,
        pricing_models: 2,
        most_important: 2,
      },
      errors: {
        title: null,
        description: null,
        vacancies: null,
        urgency: null,
        roles: null,
        working_types: null,
        previous_activities: null,
        scopes: null,
        services: null,
        recruiter_location: null,
        pricing_models: null,
        fee_ranges: null,
        most_important: null,
      },
      PROJECT_STATUSES,
      projectStatuses: statusToArray(PROJECT_STATUSES),
      closingReasons: [
        {
          text: null,
          value: null,
        },
        ...statusToArray(CLOSING_REASONS),
      ],
      businessOptions: [],
      primaryContactOptions: [],
      secondaryContactOptions: [],
      roleOptions: [],
      urgencyOptions: [
        'Starting the search',
        'We have been looking for 1-3 months',
        'We have been looking for 3+ months',
      ],
      previousActivitiesOptions: [
        { text: 'No previous activities', value: 'No previous activities' },
        { text: 'Job portal ads', value: 'Job portal ads' },
        { text: 'Social media campaigns', value: 'Social media campaigns' },
        { text: 'Targeted search', value: 'Targeted search' },
        { text: 'External recruiters / agencies', value: 'External recruiters / agencies' },
      ],
      regionsOptions: [],
      workingTypeOptions: [],
      scopesOptions: [],
      servicesOptions: [],
      RecruiterLocationOptions: [
        { text: 'Not important', value: 'Not important' },
        { text: 'Local', value: 'Local' },
      ],
      feeRangeOptions: [],
      pricingModelsOptions: [],
    }
  },
  computed: {
    ...mapState(useAuthStore, [
      'business',
      'isAdmin',
      'user',
    ]),
  },
  watch: {
    '$route.query': {
      handler: function (query, param2) {
        this.editProject = ''
        if (query.editProject) {
          this.editProject = query.editProject
          this.fetchEditData(query.editProject)
        }
      },
      deep: true,
      immediate: true,
    },
  },
  mounted () {
    if (this.isAdmin) {
      this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
        if (modalId === 'CreateProject') {
          axios.get('/admin/v2/businesses')
            .then(({ data }) => {
              const temp = this.mapValues(data)
              // temp = temp.sort((a, b) => a.name.localeCompare(b.name))
              this.businessOptions = temp
            })
          axios.get('/admin/v2/users')
            .then(({ data }) => {
              let temp = this.mapValues(data, false, 'full_name')
              temp = temp.sort((a, b) => a.full_name.localeCompare(b.full_name))
              this.primaryContactOptions = temp
              this.secondaryContactOptions = temp
            })
        }
      })
    } else {
      // Remap business secondary contacts
      this.secondaryContactOptions = this.business.contacts.map(contact => {
        return {
          text: contact.full_name,
          value: contact.id,
        }
      })
        // Remove self from 2ndary contacts
        .filter(c => c.value !== this.user.id)
    }

    axios.get('/public/v2/values/roles')
      .then(({ data }) => {
        let temp = this.mapValues(data, false, 'label')
        temp = temp.sort((a, b) => a.label.localeCompare(b.label))
        this.roleOptions = temp
      })
    axios.get('/public/v2/values/regions')
      .then(({ data }) => {
        let temp = this.mapValues(data, false, 'label')
        temp = temp.map(option => {
          option.order = GEO_REGIONS_ORDER[option.label]
          return option
        })
        temp = temp.sort((a, b) => (a.order > b.order) ? 1 : -1)
        this.regionsOptions = temp
      })
    axios.get('/public/v2/values/working_types')
      .then(({ data }) => {
        let temp = this.mapValues(data, false, 'label')
        temp = temp.sort((a, b) => a.label.localeCompare(b.label))
        this.workingTypeOptions = temp
      })
    axios.get('/public/v2/values/scopes')
      .then(({ data }) => {
        // Creating temporary variable where to hold data
        let temp = this.mapValues(data, false, 'label')
        // finding "Other" option obj. in array
        const findOtherOption = temp.find(skill => skill.text === 'Other')
        // Getting "Other" option obj. index in array
        const otherOptionIndex = temp.findIndex(skill => skill.text === 'Other')
        // Erasing "Other" obj from array
        temp.splice(otherOptionIndex, 1)
        // Sorting array by alphabetical order
        temp = temp.sort((a, b) => a.label.localeCompare(b.label))
        // Adding "Other" option at the end of array
        temp.push(findOtherOption)
        // renaming/overwriting temp variable
        this.scopesOptions = temp
      })
    axios.get('/public/v2/values/services')
      .then(({ data }) => {
        this.servicesOptions = this.mapValues(data, false, 'label')
      })
    axios.get('/public/v2/values/fee_ranges')
      .then(({ data }) => {
        let temp = this.mapValues(data, false, 'label')
        temp = temp.sort((a, b) => a.label.localeCompare(b.label))
        this.feeRangeOptions = temp
      })
    axios.get('/public/v2/values/pricing_models')
      .then(({ data }) => {
        // Creating temporary variable where to hold data
        let temp = this.mapValues(data, false, 'label')
        // finding "Other" option obj. in array
        const findOtherOption = temp.find(skill => skill.text === 'Other')
        // Getting "Other" option obj. index in array
        const otherOptionIndex = temp.findIndex(skill => skill.text === 'Other')
        // Erasing "Other" obj from array
        temp.splice(otherOptionIndex, 1)
        // Sorting array by alphabetical order
        temp = temp.sort((a, b) => a.label.localeCompare(b.label))
        // Adding "Other" option at the end of array
        temp.push(findOtherOption)
        // renaming/overwriting temp variable
        this.pricingModelsOptions = temp
      })
  },
  methods: {
    fetchEditData (id) {
      let url = `/v2/projects/${id}`
      if (this.isAdmin) {
        url = `/admin/v2/projects/${id}`
      }
      axios.get(url)
        .then(({ data }) => {
          console.log(data.previous_activities)
          data.primary_contacts = (data.primary_contacts || []).map(r => r.id)
          data.secondary_contacts = (data.secondary_contacts || []).map(r => r.id)
          data.roles = (data.roles || []).map(r => r.id)
          data.regions = (data.regions || []).map(r => r.id)
          data.working_types = (data.working_types || []).map(r => r.id)
          data.scopes = (data.scopes || []).map(r => r.id)
          data.services = (data.services || []).map(r => r.id)
          data.fee_ranges = (data.fee_ranges || []).map(r => r.id)
          data.pricing_models = (data.pricing_models || []).map(r => r.id)
          data.previous_activities = (data.previous_activities || [])
          data.business_id = [data.business_id]
          this.form = data
        })
    },
    update () {
      // Clone form data to do some modifications
      const sendData = {
        ...this.form,
      }
      // In case of edit, use business ID
      //   in case of new, use businesses array
      sendData.business_id = sendData?.business?.id || sendData.business_id[0]

      let url = `/v2/projects/${this.editProject}`
      if (this.isAdmin) {
        url = `/admin/v2/projects/${this.editProject}`
      }
      axios.put(url, sendData)
        .then(() => {
          this.modalClose()
          window.location.reload()
        })
        .catch(({ response }) => {
          this.handleErrors(response)
        })
    },
    publish () {
      const sendData = {
        ...this.form,
      }
      sendData.business_id = sendData.business_id?.[0] || sendData?.business?.id

      let url = '/v3/projects'
      if (this.isAdmin) {
        url = '/admin/v2/projects'
      }
      axios.post(url, sendData)
        .then(() => {
          this.$bvModal.msgBoxOk('Your Advisor will review it and get in touch with you within 24h, so keep an eye on chat messages.', {
            title: 'You have just created a new project. 🎉',
            size: 'md',
            buttonSize: 'lg',
            headerClass: 'mt-2 p-2 justify-content-center',
            centered: true,
          })
            .then(() => {
              this.form = { status: 'Draft', attachments: [] }
              useAuthStore().self()
                .then(() => {
                  window.location.href = `/business/${sendData.business_id}/projects`
                })
            })
        }).catch(({ response }) => {
          this.handleErrors(response)
        })
    },
    handleErrors (response) {
      let smallestStep = 4
      response?.data?.errors?.forEach(err => {
        this.errors[err.field] = err.message
        if (this.fieldStepMap[err.field] < smallestStep) {
          smallestStep = this.fieldStepMap[err.field]
        }
      })
      this.value = smallestStep
      this.$nextTick(() => {
        window.scrollTo({
          top: 0,
          left: 0,
        })
      })
    },
    mapValues (data, prepend = false, textField = 'name') {
      let dataArr = data.map(option => {
        option.value = option.id
        option.text = option[textField]
        return option
      })
      if (prepend) {
        dataArr = [
          { text: 'Select One', value: null },
          ...dataArr,
        ]
      }
      return dataArr
    },
    uploadFile (event) {
      // If no fails, then bail
      if (!event.target?.files?.length) {
        return
      }
      const formData = new FormData()
      formData.set('file', event.target.files[0])
      axios.post('/v2/upload', formData)
        .then(({ data }) => {
          // If first attachment, then just add it to there
          if (!this.form.attachments.length) {
            this.form.attachments = [data]
          } else {
            // Else, add to the end and keep all current values
            this.form.attachments = [
              ...this.form.attachments,
              data,
            ]
          }
        })
    },
    modalClose () {
      // If modal closes, remove query string from URL
      if (window.location.search) {
        this.$router.replace({ query: { } })
      }
      this.form = { status: 'Draft', attachments: [] }
    },
  },
}
</script>

<style lang="scss" scoped>
  $stepColor: #EE8B7B;

  ::v-deep .modal-class  {
    background: #F4F1F3;
  }

  // ::v-deep .modal-footer  {
  //   background: #FFF2EF;
  // }

  .stepper-wrapper {
    margin-top: auto;
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
    width: 450px;
  }
  .stepper-item {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;

    line-height: 16.8px;
    // font-weight: 400;
    font-size: 14px;
    color: #323031;
    font-family: Jost;

    @media (max-width: 768px) {
      font-size: 12px;
    }

    &::before {
      position: absolute;
      content: "";
      border-bottom: 4px solid #ccc;
      width: 70%;
      top: 8px;
      left: -35%;
      z-index: 2;
      border-radius: 10px;
    }
    &::after {
      position: absolute;
      content: "";
      border-bottom: 4px solid $stepColor;
      width: 70%;
      top: 8px;
      left: 65%;
      z-index: 2;
      border-radius: 10px;
    }
    &.active {
      font-weight: bold !important;
    }
    &.completed .step-counter {
      background-color: $stepColor;
      width: 20px;
      height: 20px;
      border: 10px solid $stepColor;
      transition: .4s ease;
    }
    &.completed::after {
      position: absolute;
      content: "";
      border-bottom: 4px solid $stepColor;
      width: 70%;
      top: 8px;
      left: 65%;
      z-index: 3;
      border-radius: 10px;
    }

    &.active .step-counter {
      border: 4px solid $stepColor;
      background-color: #faf4f2;
    }

    .step-counter {
      position: relative;
      z-index: 5;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #faf4f2;
      border: 4px solid #DADADA;
      background-color: #DADADA;
      margin-bottom: 6px;
    }
  }
  .stepper-item:first-child::before {
    content: none;
  }
  .stepper-item:last-child::after {
    content: none;
  }
  .form {
    background-color: #fff;
    box-shadow: 0px 4px 12px rgba(216, 216, 216, 0.5);
    border-radius: 12px;
    border: none;
    width: 80%;
    .short-field {
      width: 50%
    }
  }
  .b-form-tags.form-control.h-auto.custom-form-tags.mb-2.h9 {
    width: 445px !important;
  }
  .hint-local {
    color: #F7625B;
    font-family: 'Poppins';
    font-style: normal;
    font-weight: 300;
    font-size: 12px;
    line-height: 120%;
  }
</style>
